import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Modal, Form, Input, Select, InputNumber,
    DatePicker, message, Space, Tag, Popconfirm, Badge, Tabs, Alert,
    Tooltip, Typography, Row, Col, Statistic, Checkbox
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined,
    MedicineBoxOutlined, HistoryOutlined, ExperimentOutlined,
    ReloadOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useInventoryContext } from './InventoryContext';
import { InventoryMovement } from './type';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const InventoryManagement: React.FC = () => {
    const {
        items,
        loading,
        history,
        historyLoading,
        addItem,
        updateItem,
        deleteItem,
        getLowStockItems,
        fetchClinicHistory,
        fetchItems
    } = useInventoryContext();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);
    const [form] = Form.useForm();
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('stock');

    const categories = ['Medications', 'Consumables', 'Equipment', 'Supplies', 'Other'];
    const units = [
        { value: 'pieces', label: 'قطعة' },
        { value: 'strip', label: 'شريط' },
        { value: 'box', label: 'علبة' },
        { value: 'ampoule', label: 'أمبول' },
        { value: 'carton', label: 'كرتونة' },
        { value: 'bottle', label: 'زجاجة' },
        { value: 'tablet', label: 'قرص' },
        { value: 'capsule', label: 'كبسولة' },
        { value: 'ml', label: 'مل' }
    ];

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (item: any) => {
        setEditingItem(item);
        form.setFieldsValue({
            ...item,
            expirationDate: item.expirationDate ? dayjs(item.expirationDate) : null
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        try {
            await deleteItem(id);
            message.success('Item deleted successfully');
        } catch (error) {
            message.error('Failed to delete item');
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            const itemData = {
                ...values,
                expirationDate: values.expirationDate ? values.expirationDate.toISOString() : null
            };
            if (editingItem) {
                await updateItem(editingItem._id, itemData);
                message.success('Item updated successfully');
            } else {
                await addItem(itemData);
                message.success('Item added successfully');
            }
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            message.error('Failed to save item');
        }
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        if (key === 'history') {
            fetchClinicHistory({ limit: 200 });
        }
    };

    const lowStockItems = getLowStockItems();

    const stockColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) => a.name.localeCompare(b.name),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => <Tag color="blue">{category}</Tag>,
            filters: categories.map(cat => ({ text: cat, value: cat })),
            onFilter: (value: any, record: any) => record.category === value,
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (quantity: number, record: any) => {
                const isLow = quantity <= record.minStockLevel;
                const isZero = quantity === 0;
                return (
                    <span style={{ color: isZero ? '#cf1322' : isLow ? '#d46b08' : 'inherit', fontWeight: isLow ? 'bold' : 'normal' }}>
                        {quantity} {record.unit}
                        {isZero && <Tag color="red" style={{ marginLeft: 6 }}>Out of stock</Tag>}
                        {!isZero && isLow && <WarningOutlined style={{ marginLeft: 6, color: '#d46b08' }} />}
                    </span>
                );
            },
            sorter: (a: any, b: any) => a.quantity - b.quantity,
        },
        {
            title: 'Min Level',
            dataIndex: 'minStockLevel',
            key: 'minStockLevel',
            render: (level: number, record: any) => `${level} ${record.unit}`,
        },
        {
            title: 'Purchase Price',
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            render: (price: number) => `${price.toFixed(2)} EGP`,
        },
        {
            title: 'Selling Price',
            dataIndex: 'sellingPrice',
            key: 'sellingPrice',
            render: (price: number) => `${Number(price || 0).toFixed(2)} EGP`,
        },
        {
            title: 'Patient Billing',
            dataIndex: 'billableByDefault',
            key: 'billableByDefault',
            render: (billable: boolean) => billable
                ? <Tag color="green">Added to patient bill</Tag>
                : <Tag>Clinic consumption</Tag>,
        },
        {
            title: 'Supplier',
            dataIndex: 'supplier',
            key: 'supplier',
            render: (supplier: string) => supplier || '-',
        },
        {
            title: 'Expiration',
            dataIndex: 'expirationDate',
            key: 'expirationDate',
            render: (date: string) => {
                if (!date) return '-';
                const expDate = dayjs(date);
                const isExpired = expDate.isBefore(dayjs());
                const isExpiringSoon = expDate.diff(dayjs(), 'days') <= 30 && !isExpired;
                return (
                    <span style={{ color: isExpired ? 'red' : isExpiringSoon ? 'orange' : 'inherit' }}>
                        {expDate.format('YYYY-MM-DD')}
                        {isExpired && ' (Expired)'}
                        {isExpiringSoon && ' (Soon)'}
                    </span>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this item?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const historyColumns = [
        {
            title: 'Date & Time',
            dataIndex: 'usedAt',
            key: 'usedAt',
            render: (d: string) => dayjs(d).format('YYYY-MM-DD HH:mm'),
            sorter: (a: InventoryMovement, b: InventoryMovement) =>
                new Date(a.usedAt).getTime() - new Date(b.usedAt).getTime(),
            defaultSortOrder: 'descend' as const,
            width: 140
        },
        {
            title: 'Item',
            key: 'item',
            render: (_: any, record: InventoryMovement) => {
                const inv = record.inventory_id;
                if (typeof inv === 'object' && inv?.name) return <Text strong>{inv.name}</Text>;
                if (typeof inv === 'string' && inv) return <Text type="secondary">{inv}</Text>;
                return <Text type="secondary">Deleted inventory item</Text>;
            }
        },
        {
            title: 'Qty Used',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (qty: number, record: InventoryMovement) => {
                const inv = record.inventory_id;
                const unit = inv && typeof inv === 'object' ? inv.unit : '';
                return <Tag color="volcano">-{qty} {unit}</Tag>;
            },
            width: 100
        },
        {
            title: 'Patient',
            dataIndex: 'patient_name',
            key: 'patient_name',
            render: (name: string) => name || <Text type="secondary">-</Text>
        },
        {
            title: 'Service / Package',
            key: 'source',
            render: (_: any, record: InventoryMovement) => {
                if (record.package_name) return <Tag color="purple"><ExperimentOutlined /> {record.package_name}</Tag>;
                if (record.service_name) return <Tag color="blue">{record.service_name}</Tag>;
                return <Tag color="default">Manual</Tag>;
            }
        },
        {
            title: 'Type',
            dataIndex: 'action_type',
            key: 'action_type',
            render: (type: string) => {
                const colors: Record<string, string> = { service: 'blue', package: 'purple', billing: 'orange', manual: 'default' };
                return <Tag color={colors[type] || 'default'}>{type || 'manual'}</Tag>;
            },
            filters: [
                { text: 'Service', value: 'service' },
                { text: 'Package', value: 'package' },
                { text: 'Billing', value: 'billing' },
                { text: 'Manual', value: 'manual' }
            ],
            onFilter: (value: any, record: InventoryMovement) => record.action_type === value,
            width: 90
        },
        {
            title: 'Staff',
            dataIndex: 'usedBy',
            key: 'usedBy',
            render: (id: string) => <Text type="secondary" style={{ fontSize: 12 }}>{id}</Text>,
            width: 110
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            render: (notes: string) => notes
                ? <Tooltip title={notes}><InfoCircleOutlined style={{ color: '#1677ff' }} /></Tooltip>
                : null,
            width: 60
        }
    ];

    const expiredItems = items.filter(item => item.expirationDate && dayjs(item.expirationDate).isBefore(dayjs()));
    const outOfStockItems = items.filter(item => item.quantity === 0);

    return (
        <div style={{ padding: '24px' }}>
            {/* Summary stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic title="Total Items" value={items.length} prefix={<MedicineBoxOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center', background: lowStockItems.length > 0 ? '#fff7e6' : undefined }}>
                        <Statistic
                            title="Low Stock"
                            value={lowStockItems.length}
                            valueStyle={{ color: lowStockItems.length > 0 ? '#d46b08' : undefined }}
                            prefix={<WarningOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center', background: outOfStockItems.length > 0 ? '#fff1f0' : undefined }}>
                        <Statistic
                            title="Out of Stock"
                            value={outOfStockItems.length}
                            valueStyle={{ color: outOfStockItems.length > 0 ? '#cf1322' : undefined }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center', background: expiredItems.length > 0 ? '#fff1f0' : undefined }}>
                        <Statistic
                            title="Expired"
                            value={expiredItems.length}
                            valueStyle={{ color: expiredItems.length > 0 ? '#cf1322' : undefined }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Low stock alert banner */}
            {lowStockItems.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    style={{ marginBottom: 16 }}
                    message={`${lowStockItems.length} item(s) are running low on stock`}
                    description={
                        <span>
                            {lowStockItems.map(item => (
                                <Tag key={item._id} color="orange" style={{ margin: '2px' }}>
                                    {item.name}: {item.quantity} {item.unit} (min: {item.minStockLevel})
                                </Tag>
                            ))}
                        </span>
                    }
                />
            )}

            {outOfStockItems.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`${outOfStockItems.length} item(s) are out of stock`}
                    description={
                        <span>
                            {outOfStockItems.map(item => (
                                <Tag key={item._id} color="red" style={{ margin: '2px' }}>{item.name}</Tag>
                            ))}
                        </span>
                    }
                />
            )}

            <Card
                title={
                    <Space>
                        <MedicineBoxOutlined />
                        <span>Inventory Management</span>
                        {lowStockItems.length > 0 && (
                            <Badge count={lowStockItems.length} style={{ backgroundColor: '#ff4d4f' }}>
                                <Tag color="red">Low Stock</Tag>
                            </Badge>
                        )}
                    </Space>
                }
                extra={
                    <Space>
                        <Tooltip title="Refresh inventory">
                            <Button icon={<ReloadOutlined />} onClick={fetchItems} loading={loading} />
                        </Tooltip>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            Add Item
                        </Button>
                    </Space>
                }
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={[
                        {
                            key: 'stock',
                            label: (
                                <span>
                                    <MedicineBoxOutlined />
                                    Stock
                                    {lowStockItems.length > 0 && (
                                        <Badge count={lowStockItems.length} size="small" style={{ marginLeft: 6, backgroundColor: '#faad14' }} />
                                    )}
                                </span>
                            ),
                            children: (
                                <Table
                                    columns={stockColumns}
                                    dataSource={items}
                                    rowKey="_id"
                                    loading={loading}
                                    pagination={{ pageSize: 10 }}
                                    rowClassName={(record: any) => {
                                        if (record.quantity === 0) return 'ant-table-row-danger';
                                        if (record.quantity <= record.minStockLevel) return 'ant-table-row-warning';
                                        return '';
                                    }}
                                    style={{ overflowX: 'auto' }}
                                />
                            )
                        },
                        {
                            key: 'history',
                            label: (
                                <span>
                                    <HistoryOutlined />
                                    Consumption History
                                </span>
                            ),
                            children: (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                        <Button
                                            icon={<ReloadOutlined />}
                                            size="small"
                                            onClick={() => fetchClinicHistory({ limit: 200 })}
                                            loading={historyLoading}
                                        >
                                            Refresh
                                        </Button>
                                    </div>
                                    <Table
                                        columns={historyColumns}
                                        dataSource={history}
                                        rowKey="_id"
                                        loading={historyLoading}
                                        pagination={{ pageSize: 20 }}
                                        size="small"
                                        style={{ overflowX: 'auto' }}
                                        locale={{ emptyText: 'No inventory movements recorded yet.' }}
                                    />
                                </div>
                            )
                        }
                    ]}
                />
            </Card>

            {/* Add/Edit Modal */}
            <Modal
                title={editingItem ? 'Edit Inventory Item' : 'Add Inventory Item'}
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        category: 'Other',
                        quantity: 0,
                        unit: 'pieces',
                        purchasePrice: 0,
                        sellingPrice: 0,
                        billableByDefault: false,
                        minStockLevel: 0
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Item Name"
                        rules={[{ required: true, message: 'Please enter item name' }]}
                    >
                        <Input placeholder="e.g., Paracetamol 500mg" />
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="Category"
                        rules={[{ required: true, message: 'Please select category' }]}
                    >
                        <Select>
                            {categories.map(cat => (
                                <Option key={cat} value={cat}>{cat}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Space style={{ width: '100%' }} size="large">
                        <Form.Item
                            name="quantity"
                            label="Quantity"
                            rules={[{ required: true, message: 'Please enter quantity' }]}
                        >
                            <InputNumber min={0} style={{ width: 120 }} />
                        </Form.Item>

                        <Form.Item
                            name="unit"
                            label="Unit"
                            rules={[{ required: true, message: 'Please enter unit' }]}
                        >
                            <Select style={{ width: 150 }} showSearch>
                                {units.map((unit) => <Option key={unit.value} value={unit.value}>{unit.label}</Option>)}
                            </Select>
                        </Form.Item>
                    </Space>

                    <Space style={{ width: '100%' }} size="large">
                        <Form.Item name="purchasePrice" label="Purchase Price (EGP)">
                            <InputNumber min={0} step={0.01} style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item name="sellingPrice" label="Selling Price (EGP)">
                            <InputNumber min={0} step={0.01} style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item name="minStockLevel" label="Min Stock Level">
                            <InputNumber min={0} style={{ width: 120 }} />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        name="billableByDefault"
                        valuePropName="checked"
                        extra={
                            <span dir="rtl">
                                يُسجَّل الصنف افتراضيًا كاستهلاك للعيادة، وعند تفعيل هذا الخيار تُضاف تكلفته إلى فاتورة المريض.
                            </span>
                        }
                    >
                        <Checkbox>
                            <span dir="rtl">إضافة هذا الصنف إلى فاتورة المريض</span>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item name="supplier" label="Supplier">
                        <Input placeholder="Supplier name" />
                    </Form.Item>

                    <Form.Item name="expirationDate" label="Expiration Date">
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="notes" label="Notes">
                        <TextArea rows={3} placeholder="Additional notes" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingItem ? 'Update' : 'Add'} Item
                            </Button>
                            <Button onClick={() => { setIsModalVisible(false); form.resetFields(); }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default InventoryManagement;
